import React, { useState, useEffect } from "react";
import MoreHorizIcon from "@material-ui/icons/MoreHoriz";
import AppMenu from "../../menu";
import TablesData from "../globalTable";
import TableItem from "../globalTable/tableItem";
import { SearchOnly } from "../globalTable/searchandfilter";
import Loader from "../../common/Loader";
import { axiosInstance } from "../../../utils/axiosInstance";
import notification from "../../common/Alert";
import Button from "../../common/Button";
import ReusableAdminModal from "../modals/ReusableAdminModal";
import AssignRoleModal from "../modals/AssignRoleModal";

export default function AdminTable() {
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adminModalOpen, setAdminModalOpen] = useState(false);
  const [roleModalOpen, setRoleModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create"); // create, edit, delete
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);

  // Fetch admins from API
  const fetchAdmins = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get("/v1/admin/admins");
      setAdmins(response.data?.admins || []);
    } catch (error) {
      notification.notication("error", "Failed to fetch admins", "Error");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchAdmins();
  }, []);

  // Fetch roles from API
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await axiosInstance.get("/v1/admin/roles");
        setRoles(response.data?.data || []);
      } catch (error) {
        notification.notication("error", "Failed to fetch roles", "Error");
      }
    };
    fetchRoles();
  }, []);

  // Handle search input
  const handleSearch = (e) => {
    setSearch(e.target.value);
  };

  // Filter admins based on search input
  const filteredAdmins = admins.filter(
    (admin) =>
      `${admin?.first_name} ${admin?.last_name}`
        .toLowerCase()
        .includes(search.toLowerCase()) ||
      admin?.email.toLowerCase().includes(search.toLowerCase()) ||
      admin?.id.toString().includes(search)
  );

  // Handle checkbox selection
  const handleClick = (id) => {
    setSelected((prev) =>
      prev.includes(id)
        ? prev.filter((selectedId) => selectedId !== id)
        : [...prev, id]
    );
  };

  // Handle select all checkboxes
  const handleSelectAll = () => {
    setSelected(
      selected.length === admins.length ? [] : admins.map((admin) => admin.id)
    );
  };

  // Open modal for create/edit/delete admin
  const openAdminModal = (mode, admin = null) => {
    setModalMode(mode);
    setSelectedAdmin(admin);
    setAdminModalOpen(true);
  };

  // Open modal for assigning role
  const openRoleModal = (admin) => {
    setSelectedUser(admin);
    setRoleModalOpen(true);
  };

  // Close admin modal
  const closeAdminModal = () => {
    setAdminModalOpen(false);
    setSelectedAdmin(null);
    setModalMode("create");
  };

  // Close role modal
  const closeRoleModal = () => {
    setRoleModalOpen(false);
    setSelectedUser(null);
  };

  // Handle create/edit admin
  const handleSubmitAdmin = async (id, payload) => {
    try {
      if (modalMode === "create") {
        const response = await axiosInstance.post("/v1/admin/admins", payload);
        if(response?.status){

          notification.notication(
            "success",
            "Admin created successfully",
            "Success",
            { timer: 3000 }
          );
          fetchAdmins();
        }
      } else if (modalMode === "edit") {
        const response = await axiosInstance.put(
          `/v1/admin/admins/${id}`,
          payload
        );
        if(response?.status){

          notification.notication(
            "success",
            "Admin updated successfully",
            "Success",
            { timer: 3000 }
          );
          fetchAdmins();
        }
      }
    } catch (error) {
      notification.notication("error", "Failed to fetch admins", "Error");

      throw error;
    }
  };

  // Handle assign role
  const handleAssignRole = async (userId, roleId) => {
    try {
      const response = await axiosInstance.post(
        `/v1/admin/roles/${userId}/assign`,
        { role_id: roleId }
      );
      if (response?.data?.status) {
        
        notification.notication(
          "success",
          "Role assigned successfully",
          "Success",
          { timer: 3000 }
        );
        fetchAdmins();
      }
    } catch (error) {
      notification.notication("error", "Failed to assign role", "Error");
      throw error;
    }
  };

  // Handle delete admin
  const handleDeleteAdmin = async (id) => {
    try {
      await axiosInstance.delete(`/v1/admin/admins/${id}`);
      notification.notication("success", "Admin deleted successfully", "Success", {
        timer: 3000,
      });
      fetchAdmins();
    } catch (error) {
      notification.notication("error", "Failed to delete admin", "Error");
      throw error;
    }
  };

  return (
    <div>
      <div
        style={{ display: "flex", alignItems: "center", marginBottom: "2rem" }}
      >
        <SearchOnly handleSearch={handleSearch} searchWord={search} />
        <Button onClick={() => openAdminModal("create")}>Create Admin</Button>
      </div>
      <Loader visible={loading} />
      <TableItem
        userType="Admin"
        checkBoxes
        tabHeadLabel={["ID", "Name", "Email", "Created At", ""]}
        action
        tableSize
        tabToolBar
        tabHeadCheck
        numSelectedItem={selected.length}
        rowCount={admins.length}
        numSelected={selected.length}
        onSelectAllClick={handleSelectAll}
        tabBodyData={filteredAdmins.map((admin, i) => (
          <TablesData
            key={admin.id}
            labelId={`enhanced-table-checkbox-${i}`}
            handleItemClick={() => handleClick(admin?.id)}
            aria-checked={selected.includes(admin?.id)}
            isItemSelected={selected.includes(admin?.id)}
            checkBoxes
            listItem={[
              admin.id,
              <span style={{ textTransform: "capitalize" }}>
                {admin?.first_name} {admin?.last_name}
              </span>,
              new Date(admin?.created_at).toLocaleDateString(),
              admin?.email,
            ]}
            action={
              <AppMenu
                menu_name_or_icon={<MoreHorizIcon />}
                item_names={[
                  { Edit: () => openAdminModal("edit", admin) },
                  { Delete: () => openAdminModal("delete", admin) },
                  { "Assign Role": () => openRoleModal(admin) },
                ]}
              />
            }
          />
        ))}
      />
      <ReusableAdminModal
        isOpen={adminModalOpen}
        onClose={closeAdminModal}
        mode={modalMode}
        providerData={selectedAdmin}
        onSubmit={handleSubmitAdmin}
        onDelete={handleDeleteAdmin}
      />
      <AssignRoleModal
        isOpen={roleModalOpen}
        onClose={closeRoleModal}
        user={selectedUser}
        roles={roles}
        onAssign={handleAssignRole}
      />
    </div>
  );
}
